"use client";

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";
import PersonalInfoForm from "./PersonalInfoForm";
import OtpVerificationForm from "./OTPVerification";
import CreatePasswordForm from "./CreatePasswordForm";
import SuccessStep from "./RegistrationSuccessModal";
import { useDispatch } from "react-redux";
import { setReferralAuth } from "../../../store/slice/ReferralAuthSlice";
import { login } from "../../../store/slice/AuthSlice";
import {
  Backdrop,
  Box,
  Button,
  Fade,
  Modal,
  Paper,
  Typography,
} from "@mui/material";

interface FormData {
  name: string;
  surname: string;
  phone: string;
  email: string;
  businessName: string;
  organisationCategory: string;
  agreeTerms: boolean;
  paymentMethod: string;
  bankName: string;
  bankNumber: string;
  accountHolderName: string;
  idPhoto: File | null;
}

interface FormErrors {
  name?: string;
  surname?: string;
  phone?: string;
  email?: string;
  businessName?: string;
  organisationCategory?: string;
  agreeTerms?: string;
  paymentMethod?: string;
}

interface RegistrationFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CompleteProfileResponse {
  message: string;
  referral_partner: { id: number };
  access_token: string;
  refresh_token: string;
  user: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_superuser: boolean;
    permissions: string[];
    referral_partner: number;
  };
}

type Step = "personalInfo" | "otp" | "createPassword" | "success";

const STEP_LABELS: Record<Step, string> = {
  personalInfo: "Personal info",
  otp: "Email verification",
  createPassword: "Create password",
  success: "Complete",
};

function extractErrorMessage(err: unknown, fallback: string): string {
  const responseData = (
    err as { response?: { status?: number; data?: unknown } }
  )?.response;
  if (!responseData) return fallback;
  const data = responseData.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object") {
    const detail = (data as { detail?: string }).detail;
    if (detail) return detail;
    try {
      const messages = Object.entries(data as Record<string, unknown>)
        .map(([key, value]) => {
          if (Array.isArray(value)) return `${key}: ${value.join(", ")}`;
          if (typeof value === "string") return `${key}: ${value}`;
          return `${key}: ${String(value)}`;
        })
        .join(" | ");
      if (messages) return messages;
    } catch {
      return JSON.stringify(data);
    }
  }
  return fallback;
}

const EMPTY_FORM: FormData = {
  name: "",
  surname: "",
  phone: "+263",
  email: "",
  businessName: "",
  organisationCategory: "",
  agreeTerms: false,
  paymentMethod: "",
  bankName: "",
  bankNumber: "",
  accountHolderName: "",
  idPhoto: null,
};

// ── Resume prompt modal ───────────────────────────────────────────────────────

interface ResumePromptProps {
  open: boolean;
  step: Step;
  email: string;
  onContinue: () => void;
  onStartOver: () => void;
}

function ResumePrompt({
  open,
  step,
  email,
  onContinue,
  onStartOver,
}: ResumePromptProps) {
  const stepIndex =
    (["personalInfo", "otp", "createPassword", "success"] as Step[]).indexOf(
      step,
    ) + 1;

  return (
    <Modal
      open={open}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 300 } }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            outline: "none",
            width: "90%",
            maxWidth: 400,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Icon + heading */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "warning.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ fontSize: 20 }}>⏸</Typography>
              </Box>
              <Box>
                <Typography fontWeight={600} fontSize={15}>
                  Registration in progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You left off at step {stepIndex} of 4 — {STEP_LABELS[step]}
                </Typography>
              </Box>
            </Box>

            {/* Info chip showing saved email */}
            {email && (
              <Box
                sx={{
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography fontSize={12} color="text.secondary">
                  Registering as
                </Typography>
                <Typography fontSize={13} fontWeight={500} noWrap>
                  {email}
                </Typography>
              </Box>
            )}

            {/* Actions */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={onContinue}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Continue where I left off
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                onClick={onStartOver}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  color: "text.secondary",
                  borderColor: "divider",
                }}
              >
                Start over
              </Button>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Modal>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function RegistrationForm({
  open,
  onClose,
}: RegistrationFormProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = useState<Step>("personalInfo");
  const [createdPartnerId, setCreatedPartnerId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({ ...EMPTY_FORM });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Whether to show the resume-or-start-over prompt instead of the actual step
  const [showResumePrompt, setShowResumePrompt] = useState(false);

  // Track if process has been started at all (so we know when to show the prompt)
  const hasProgress = useRef(false);

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setErrors({});
    setCreatedPartnerId(null);
    setStep("personalInfo");
    hasProgress.current = false;
  };

  // When modal opens: show resume prompt if there's saved progress, else go straight in
  useEffect(() => {
    if (open) {
      if (hasProgress.current && step !== "success") {
        setShowResumePrompt(true);
      }
      // If no progress yet, just open normally — nothing to resume
    } else {
      setShowResumePrompt(false);
    }
  }, [open, step]);

  const handleChange = (
    field: keyof FormData,
    value: string | boolean | File | null, // widen the type
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.surname.trim()) newErrors.surname = "Surname is required";
    if (!formData.phone.trim() || formData.phone === "+263")
      newErrors.phone = "Phone number is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email";
    }
    if (!formData.businessName.trim())
      newErrors.businessName = "Business name is required";
    if (!formData.organisationCategory)
      newErrors.organisationCategory = "Select a category";
    if (!formData.agreeTerms)
      newErrors.agreeTerms = "You must agree to the terms";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep1()) return;
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await apiService.post(
        "/api/refferal-program/register/",
        {
          first_name: formData.name.trim(),
          last_name: formData.surname.trim(),
          email: formData.email.trim(),
          phone_number: formData.phone.replace(/\s+/g, "").trim(),
          business_name: formData.businessName.trim(),
          category: formData.organisationCategory,
          agreed_to_terms: formData.agreeTerms,
        },
        true,
      );
      toast.success("Account created! Please check your email for the OTP.");
      hasProgress.current = true;
      setStep("otp");
    } catch (err) {
      toast.error(
        extractErrorMessage(err, "Registration failed. Please try again."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (otp: string) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await apiService.post(
        "/api/refferal-program/verify-code/",
        { email: formData.email.trim(), code: otp },
        true,
      );
      toast.success("Email verified successfully!");
      setStep("createPassword");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Invalid code. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    try {
      await apiService.post(
        "/api/refferal-program/register/",
        { email: formData.email.trim() },
        true,
      );
      toast.info("Code resent to " + formData.email);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to resend code."));
    }
  };

  const handleCreatePassword = async (
    password: string,
    confirmPassword: string,
  ) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await apiService.post<CompleteProfileResponse>(
        "/api/refferal-program/complete-profile/",
        {
          email: formData.email.trim(),
          password,
          confirm_password: confirmPassword,
          payment_method: formData.paymentMethod,
          bank_name: formData.bankName,
          bank_number: formData.bankNumber,
          account_holder_name: formData.accountHolderName,
        },
        true,
      );

      if (response.data?.referral_partner?.id) {
        dispatch(
          setReferralAuth({
            user: {
              ...response.data.user,
              referral_partner: response.data.referral_partner.id,
            },
            access_token: response.data.access_token,
            refresh_token: response.data.refresh_token,
          }),
        );
        dispatch(
          login({
            user: {
              username: response.data.user.username,
              first_name: response.data.user.first_name,
              last_name: response.data.user.last_name,
              is_supper_user: "",
              permissions: response.data.user.permissions,
            },
            access_token: response.data.access_token,
            refresh_token: response.data.refresh_token,
            is_2fa_enabled: false,
          }),
        );
        toast.success("Profile complete! Welcome to the partner program.");
        setCreatedPartnerId(String(response.data.referral_partner.id));
        setStep("success");
      } else {
        toast.error("Profile created, but partner ID was missing.");
      }
    } catch (err) {
      toast.error(
        extractErrorMessage(err, "Something went wrong. Please try again."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDashboardClick = () => {
    if (createdPartnerId) {
      navigate(`/referral-dashboard/${createdPartnerId}`);
      resetForm();
    }
  };

  const handleGoToStep = (target: Step) => setStep(target);

  // Resume prompt actions
  const handleResume = () => setShowResumePrompt(false);
  const handleStartOver = () => {
    resetForm();
    setShowResumePrompt(false);
  };

  const isStepOpen = (target: Step) =>
    open && !showResumePrompt && step === target;

  return (
    <>
      {/* Resume or start over prompt */}
      <ResumePrompt
        open={open && showResumePrompt}
        step={step}
        email={formData.email}
        onContinue={handleResume}
        onStartOver={handleStartOver}
      />

      <PersonalInfoForm
        open={isStepOpen("personalInfo")}
        onClose={onClose}
        formData={formData}
        errors={errors}
        onChange={handleChange}
        onNext={handleNext}
        isSubmitting={isSubmitting}
      />
      <OtpVerificationForm
        open={isStepOpen("otp")}
        onClose={onClose}
        phone={formData.email}
        onVerify={handleVerify}
        onResend={handleResend}
        isSubmitting={isSubmitting}
        onBack={() => handleGoToStep("personalInfo")}
      />
      <CreatePasswordForm
        open={isStepOpen("createPassword")}
        onClose={onClose}
        email={formData.email}
        onSubmit={handleCreatePassword}
        isSubmitting={isSubmitting}
        onBack={() => handleGoToStep("otp")}
      />
      <SuccessStep
        open={isStepOpen("success")}
        onDashboardClick={handleDashboardClick}
      />
    </>
  );
}
